import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import {
  ChevronDown,
  LogOut,
  User,
  Shield,
  Search,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// ==================== SIDEBAR ====================

function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  
  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const navItems = [
    { path: "/", label: "Dashboard" },
    { path: "/inbox", label: "Inbox" },
    { path: "/jobs", label: "Jobs" },
    { path: "/products", label: "Products" },
    { path: "/customers", label: "Customers" },
  ];

  const modeItems = [
    { path: "/quote", label: "New Quote", accent: "business", testid: "sidebar-new-quote" },
  ];

  const settingsItems = isAdmin ? [
    { path: "/settings", label: "Settings" },
  ] : [];

  return (
    <aside className="w-[220px] h-screen bg-workspace-sidebar border-r border-workspace-border flex flex-col shrink-0">
      {/* Logo */}
      <div className="h-14 px-4 flex items-center border-b border-workspace-border">
        <Link to="/" className="flex items-center">
          <Logo size="sm" showText={true} />
        </Link>
      </div>
      
      {/* Navigation */}
      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
        {/* Main Nav */}
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            data-testid={`sidebar-${item.label.toLowerCase()}`}
            className={`
              block px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150
              ${isActive(item.path)
                ? "bg-workspace-active text-workspace-text-primary"
                : "text-workspace-text-secondary hover:bg-workspace-hover hover:text-workspace-text-primary"
              }
            `}
          >
            {item.label}
          </Link>
        ))}

        {/* Divider */}
        <div className="my-3 border-t border-workspace-border" />

        {/* Mode Nav - New Quote */}
        {modeItems.map((item) => (
          <Link
            key={item.path}
            to={item.path === "/quote" ? "/quote?new=business" : item.path}
            data-testid={item.testid || `sidebar-${item.label.toLowerCase()}`}
            className={`
              block px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150
              ${isActive(item.path)
                ? item.accent === "retail"
                  ? "bg-orange-50 text-orange-700"
                  : "bg-blue-50 text-blue-700"
                : "text-workspace-text-secondary hover:bg-workspace-hover hover:text-workspace-text-primary"
              }
            `}
          >
            {item.label}
          </Link>
        ))}

        {/* Divider - Only if there are settings items */}
        {settingsItems.length > 0 && (
          <div className="my-3 border-t border-workspace-border" />
        )}

        {/* Settings Nav */}
        {settingsItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            data-testid={`sidebar-${item.label.toLowerCase()}`}
            className={`
              block px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150
              ${isActive(item.path)
                ? "bg-workspace-active text-workspace-text-primary"
                : "text-workspace-text-secondary hover:bg-workspace-hover hover:text-workspace-text-primary"
              }
            `}
          >
            {item.label}
          </Link>
        ))}
      </nav>

      {/* User section at bottom */}
      <div className="p-3 border-t border-workspace-border">
        <UserMenu compact />
      </div>
    </aside>
  );
}

// ==================== TOP BAR ====================

function TopBar({ title, actions }) {
  return (
    <header className="h-14 bg-white border-b border-workspace-border px-6 flex items-center justify-between shrink-0">
      {/* Left: Page Title */}
      <h1 className="text-lg font-semibold text-workspace-text-primary">
        {title}
      </h1>

      {/* Right: Actions + Search + User */}
      <div className="flex items-center gap-3">
        {actions}
      </div>
    </header>
  );
}

// ==================== USER MENU ====================

function UserMenu({ compact = false }) {
  const { user, logout, isAdmin } = useAuth();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="user-menu-btn"
          className={`
            flex items-center gap-2 rounded-md text-sm
            transition-colors duration-150 w-full
            ${compact 
              ? "px-2 py-2 hover:bg-workspace-hover" 
              : "px-3 py-2 bg-white border border-workspace-border hover:bg-gray-50"
            }
          `}
        >
          <div
            className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
              isAdmin ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-700"
            }`}
          >
            {isAdmin ? <Shield className="w-3.5 h-3.5" /> : <User className="w-3.5 h-3.5" />}
          </div>
          <span className="flex-1 text-left text-workspace-text-primary font-medium truncate">
            {user?.username}
          </span>
          <ChevronDown className="w-3.5 h-3.5 text-workspace-text-secondary shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48 rounded-md shadow-lg bg-white border border-workspace-border p-1">
        <div className="px-3 py-2 border-b border-workspace-border mb-1">
          <p className="font-medium text-sm text-workspace-text-primary">{user?.username}</p>
          <p className="text-xs text-workspace-text-secondary capitalize">{user?.role}</p>
        </div>
        <DropdownMenuItem
          onClick={logout}
          className="text-red-600 focus:text-red-600 focus:bg-red-50 rounded cursor-pointer"
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ==================== WORKSPACE LAYOUT ====================

export default function WorkspaceLayout({ children, title, actions }) {
  return (
    <div className="h-screen flex bg-workspace-bg overflow-hidden">
      {/* Sidebar */}
      <Sidebar />
      
      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Bar */}
        <TopBar title={title} actions={actions} />
        
        {/* Content */}
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}

// Export components for flexible usage
export { Sidebar, TopBar, UserMenu };
